import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { AlertOctagon, Download, Plus, Upload, X, Search, FileCheck2, PenLine } from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";

const inp = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20";
const hoy = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Bogota" });

function estadoLl(x) {
  if (x.estado === "firmado" || !x.faltan?.length) return { t: "Firmado", c: "bg-emerald-50 text-emerald-700" };
  if (x.faltan.length > 1) return { t: "Pendiente de firmas", c: "bg-amber-50 text-amber-700" };
  return { t: x.faltan[0] === "empresa" ? "Pendiente de firma de la empresa usuaria" : "Pendiente de firma del empleado", c: "bg-amber-50 text-amber-700" };
}

export function FilaLlamado({ x, acciones }) {
  const e = estadoLl(x);
  return (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`llamado-${x.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800">{x.motivos?.join(" · ")}</p>
        <p className="text-xs text-slate-500">
          {x.fecha} · Art. {x.articulos?.map((a) => a.numero).join(", ")} del Reglamento{x.empresa_usuaria ? ` · ${x.empresa_usuaria}` : ""}
          {x.estado !== "firmado" ? "" : x.tiene_acta_fisica ? " · firmado a mano" : " · firmado en Roge"}
        </p>
        {x.faltan?.length > 0 && <p className="text-xs font-medium text-amber-700" data-testid={`llamado-faltan-${x.id}`}>Falta la firma de: {x.faltan.map((f) => (f === "empresa" ? "la empresa usuaria (jefe inmediato)" : "el empleado")).join(" y ")}</p>}
        {x.jefe_firmante && <p className="text-xs text-slate-500" data-testid={`llamado-jefe-${x.id}`}>Jefe inmediato: {x.jefe_firmante.nombre} · {x.jefe_firmante.cargo}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${e.c}`} data-testid={`llamado-estado-${x.id}`}>{e.t}</span>
        <button onClick={() => downloadFile(`/llamados/${x.id}/documento`, `llamado_atencion_${x.cedula}.pdf`)} data-testid={`llamado-descargar-${x.id}`}
          className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> Acta</button>
        {x.tiene_acta_fisica && (
          <button onClick={() => downloadFile(`/llamados/${x.id}/acta-fisica`, `llamado_firmado_${x.cedula}.${({ "image/jpeg": "jpg", "image/png": "png" })[x.acta_tipo] || "pdf"}`)} data-testid={`llamado-fisica-${x.id}`}
            className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"><FileCheck2 className="h-3.5 w-3.5" /> Firmada a mano</button>
        )}
        {acciones}
      </div>
    </div>
  );
}

function SelectorArticulos({ arts, sel, setSel }) {
  const [q, setQ] = useState("");
  const lista = useMemo(() => arts.filter((a) => `${a.capitulo} ${a.lineas.join(" ")}`.toLowerCase().includes(q.toLowerCase())), [arts, q]);
  const toggle = (n) => setSel((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
  return (
    <div>
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar artículo: horario, inasistencia, uniforme…" className={`${inp} pl-9`} data-testid="llamado-buscar-articulo" />
      </div>
      <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2" data-testid="llamado-lista-articulos">
        {lista.map((a) => (
          <label key={a.numero} className={`flex cursor-pointer gap-2 rounded-lg p-2 text-sm ${sel.includes(a.numero) ? "bg-vino-50" : "hover:bg-slate-50"}`}>
            <input type="checkbox" checked={sel.includes(a.numero)} onChange={() => toggle(a.numero)} className="mt-0.5 h-4 w-4 accent-vino-700" data-testid={`llamado-articulo-${a.numero}`} />
            <span className="min-w-0">
              <span className="block font-medium text-slate-800">{a.lineas[0]}</span>
              <span className="block text-xs text-slate-400">{a.capitulo}</span>
              <span className="line-clamp-2 text-xs text-slate-500">{a.lineas.slice(1).join(" ")}</span>
            </span>
          </label>
        ))}
        {!lista.length && <p className="p-2 text-sm text-slate-400">No hay artículos que coincidan.</p>}
      </div>
      <p className="mt-1 text-xs text-slate-500" data-testid="llamado-articulos-elegidos">{sel.length ? `Elegidos: art. ${sel.join(", ")}` : "Elige uno o varios artículos."}</p>
    </div>
  );
}

function NuevoLlamado({ vid, onClose, onSaved }) {
  const [arts, setArts] = useState(null);
  const [f, setF] = useState({ fecha: hoy(), lugar: "Bogotá D.C.", motivos: "", hechos: "" });
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/llamados/articulos-rit").then((r) => setArts(r.data)).catch(() => setArts({ disponible: false, articulos: [] })); }, []);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const expedir = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/vinculaciones/${vid}/llamados`, { ...f, motivos: f.motivos.split("\n").map((m) => m.trim()).filter(Boolean), articulos: sel });
      toast.success("Llamado de atención expedido. Ya está en el portal del empleado para firmar.");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-0 sm:items-center sm:p-4" data-testid="modal-nuevo-llamado">
      <form onSubmit={expedir} className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl bg-white p-5 sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Nuevo llamado de atención</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="llamado-cerrar"><X className="h-5 w-5" /></button>
        </div>
        {!arts ? <Spinner /> : !arts.disponible ? (
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">Primero debe estar publicado el Reglamento Interno de Trabajo.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-medium text-slate-500">Fecha del acta *<input type="date" required value={f.fecha} onChange={(e) => set("fecha", e.target.value)} className={`${inp} mt-1`} data-testid="llamado-fecha" /></label>
              <label className="text-xs font-medium text-slate-500">Lugar *<input required value={f.lugar} onChange={(e) => set("lugar", e.target.value)} className={`${inp} mt-1`} data-testid="llamado-lugar" /></label>
            </div>
            <label className="block text-xs font-medium text-slate-500">Motivo: obligación incumplida * <span className="font-normal text-slate-400">(una por línea)</span>
              <textarea required rows={2} value={f.motivos} onChange={(e) => set("motivos", e.target.value)} placeholder="Reiteradas llegadas tarde durante el mes de octubre" className={`${inp} mt-1`} data-testid="llamado-motivos" />
            </label>
            <label className="block text-xs font-medium text-slate-500">Hechos *
              <textarea required rows={5} value={f.hechos} onChange={(e) => set("hechos", e.target.value)} placeholder="Describe qué pasó, cuándo y cómo se evidenció." className={`${inp} mt-1`} data-testid="llamado-hechos" />
            </label>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Normas infringidas: artículos del Reglamento (versión {arts.version}) *</p>
              <SelectorArticulos arts={arts.articulos} sel={sel} setSel={setSel} />
            </div>
            <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">Roge toma del trabajador el nombre, el documento, el cargo y la empresa usuaria. El acta sale en el membrete. Si el trabajador tiene empresa usuaria, firma como jefe inmediato un usuario de esa empresa desde su portal; si es de planta, firma Gerencia. El empleado firma desde su portal, o se sube el acta firmada a mano.</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600">Cancelar</button>
              <button disabled={busy || !sel.length} className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60" data-testid="llamado-expedir">{busy && <Spinner className="h-4 w-4" />} Expedir llamado de atención</button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

function SubirActa({ x, onSaved }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const subir = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.post(`/llamados/${x.id}/acta-fisica`, fd); toast.success("Acta firmada cargada"); onSaved(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => subir(e.target.files?.[0])} data-testid={`llamado-input-acta-${x.id}`} />
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`llamado-subir-acta-${x.id}`} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
        {busy ? <Spinner className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />} {x.tiene_acta_fisica ? "Reemplazar firmada a mano" : "Subir firmada a mano"}
      </button>
    </>
  );
}

export function LlamadosAtencion({ vid }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [nuevo, setNuevo] = useState(false);
  const editor = ["admin", "gestor"].includes(user?.role);
  const cargar = () => api.get(`/vinculaciones/${vid}/llamados`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { cargar(); }, [vid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="llamados-atencion">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <AlertOctagon className="h-4 w-4 text-vino-700" />
          <h3 className="text-base font-semibold text-slate-800">Llamados de atención</h3>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600" data-testid="llamados-total">{items.length}</span>
        </div>
        {editor && <button onClick={() => setNuevo(true)} data-testid="btn-nuevo-llamado" className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-2 text-sm font-semibold text-white hover:bg-vino-800"><Plus className="h-4 w-4" /> Nuevo llamado de atención</button>}
      </div>
      <p className="mb-2 text-xs text-slate-400">Lo ven HUMAN, el empleado en su portal y su empresa usuaria.</p>
      {items.length === 0 ? <p className="text-sm text-slate-500" data-testid="llamados-vacio">Este trabajador no tiene llamados de atención.</p> : (
        <div className="divide-y divide-slate-100">
          {items.map((x) => <FilaLlamado key={x.id} x={x} acciones={editor && <SubirActa x={x} onSaved={cargar} />} />)}
        </div>
      )}
      {nuevo && <NuevoLlamado vid={vid} onClose={() => setNuevo(false)} onSaved={() => { setNuevo(false); cargar(); }} />}
    </div>
  );
}

function FirmarComoJefe({ x, onClose, onSigned }) {
  const { user } = useAuth();
  const pad = useRef(null);
  const [vacio, setVacio] = useState(true);
  const [cargo, setCargo] = useState("");
  const [acepta, setAcepta] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const firmar = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/cliente/llamados/${x.id}/firmar`, { trazo: pad.current.imagen(), cargo, acepta });
      toast.success("Firmaste el acta como jefe inmediato");
      onSigned();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 sm:items-center sm:p-4" data-testid="modal-firmar-jefe">
      <div className="max-h-[94vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Firmar como jefe inmediato</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="firmar-jefe-cerrar"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-3 text-sm text-slate-600">Acta de <strong>{x.nombre}</strong>. <button onClick={() => downloadFile(`/llamados/${x.id}/documento`, "llamado_atencion.pdf")} className="font-semibold text-vino-700 underline" data-testid="firmar-jefe-leer">Leer acta</button></p>
        <p className="mb-1 text-xs font-medium text-slate-500">Nombre que aparecerá en el acta</p>
        <p className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800" data-testid="firmar-jefe-nombre">{user?.name}</p>
        <input value={cargo} onChange={(e) => setCargo(e.target.value)} placeholder="Tu cargo (ej. Supervisor de planta)" className={`${inp} mb-3`} data-testid="firmar-jefe-cargo" />
        <SignaturePad ref={pad} onChange={setVacio} />
        <button type="button" onClick={() => pad.current.limpiar()} className="mt-2 text-xs text-slate-500">Borrar firma</button>
        <label className="mt-3 flex gap-2 text-xs text-slate-600"><input type="checkbox" checked={acepta} onChange={(e) => setAcepta(e.target.checked)} className="mt-0.5 h-5 w-5 accent-vino-700" data-testid="firmar-jefe-acepta" /> Declaro que informé al empleado del contenido del acta y autorizo el uso de mi firma electrónica solo para este documento (Ley 527 de 1999).</label>
        {error && <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="firmar-jefe-error">{error}</p>}
        <button onClick={firmar} disabled={busy || vacio || !acepta || cargo.trim().length < 3} data-testid="firmar-jefe-enviar"
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-vino-700 px-4 py-3.5 text-base font-semibold text-white hover:bg-vino-800 disabled:opacity-60">{busy ? <Spinner className="h-5 w-5" /> : <PenLine className="h-5 w-5" />} Firmar acta</button>
      </div>
    </div>
  );
}

export function LlamadosCliente({ vid }) {
  const [items, setItems] = useState(null);
  const [firmando, setFirmando] = useState(null);
  const cargar = () => api.get(`/cliente/empleados/${vid}/llamados`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { cargar(); }, [vid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="llamados-cliente">
      <div className="mb-2 flex items-center gap-2"><AlertOctagon className="h-4 w-4 text-vino-700" /><h3 className="text-base font-semibold text-slate-800">Llamados de atención</h3></div>
      {items.length === 0 ? <p className="text-sm text-slate-500">Este trabajador no tiene llamados de atención.</p> : (
        <div className="divide-y divide-slate-100">{items.map((x) => <FilaLlamado key={x.id} x={x} acciones={x.faltan?.includes("empresa") && (
          <button onClick={() => setFirmando(x)} data-testid={`cliente-firmar-llamado-${x.id}`} className="flex items-center gap-1 rounded-lg bg-vino-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-vino-800"><PenLine className="h-3.5 w-3.5" /> Firmar como jefe inmediato</button>
        )} />)}</div>
      )}
      {firmando && <FirmarComoJefe x={firmando} onClose={() => setFirmando(null)} onSigned={() => { setFirmando(null); cargar(); }} />}
    </div>
  );
}
